import React, { useRef, useState } from 'react';
import {
  X,
  Printer,
  Download,
  CheckCircle2,
  Share2,
  Copy,
  FileCheck,
  Building,
  Loader2,
  Check,
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { CommonFeeBill } from '../types';
import { ReceiptDocument } from './ReceiptDocument';

interface Props {
  bill: CommonFeeBill | null;
  onClose: () => void;
}

export const ReceiptModal: React.FC<Props> = ({ bill, onClose }) => {
  const receiptRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!bill) return null;

  // Handle Browser Native Print Dialog
  const handlePrint = () => {
    window.print();
  };

  // Handle High-Resolution PDF Export
  const handleDownloadPDF = async () => {
    if (!receiptRef.current) return;
    try {
      setIsGeneratingPDF(true);

      // Render document to canvas with high resolution scale
      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.98);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgProps = pdf.getImageProperties(imgData);
      const ratio = imgProps.width / imgProps.height;
      
      // Calculate dimensions with margin
      const margin = 10; // mm
      const printableWidth = pdfWidth - margin * 2;
      const printableHeight = printableWidth / ratio;

      // Fit nicely onto single A4 page
      const finalY = printableHeight < pdfHeight - margin * 2 ? margin + 5 : margin;

      pdf.addImage(imgData, 'JPEG', margin, finalY, printableWidth, printableHeight);

      // Clean file naming
      const houseClean = bill.house_number.replace(/[\/\\]/g, '-');
      const periodClean = bill.period.replace(/\s+/g, '_');
      const fileName = `ใบเสร็จรับเงิน_นราวดี_${houseClean}_${periodClean}.pdf`;

      pdf.save(fileName);
    } catch (err) {
      console.error('Error generating PDF:', err);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleCopyLink = () => {
    const receiptNum = bill.receipt_number || `RC-256908-${bill.house_number.replace(/[^0-9]/g, '')}-4821`;
    const shareText = `ใบเสร็จรับเงินค่าส่วนกลาง โครงการนราวดี อิลิแกนซ์\nบ้านเลขที่: ${bill.house_number} (${bill.resident_name})\nงวด: ${bill.period}\nยอดชำระ: ฿${bill.amount.toLocaleString()} บาท\nเลขที่ใบเสร็จ: ${receiptNum}\nสถานะ: ชำระและออกใบเสร็จสมบูรณ์แล้ว`;
    navigator.clipboard.writeText(shareText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/75 backdrop-blur-xs overflow-y-auto print:p-0 print:bg-white">
      <div className="bg-slate-100 rounded-3xl max-w-4xl w-full shadow-2xl border border-slate-200 overflow-hidden my-4 flex flex-col max-h-[92vh] print:max-h-none print:m-0 print:border-none print:shadow-none print:bg-white print:rounded-none">
        
        {/* Top Action Bar (Hidden on Print) */}
        <div className="px-5 py-3.5 bg-slate-900 text-white flex flex-wrap items-center justify-between gap-3 shrink-0 print:hidden">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-blue-600/30 text-blue-400 border border-blue-500/30">
              <FileCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm sm:text-base font-bold">ใบเสร็จรับเงินอิเล็กทรอนิกส์ (E-Receipt)</h3>
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-semibold border border-emerald-500/30 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> อนุมัติแล้ว
                </span>
              </div>
              <p className="text-xs text-slate-400">
                บ้านเลขที่ {bill.house_number} • ประจำงวด {bill.period}
              </p>
            </div>
          </div>

          {/* Action Buttons Toolbar */}
          <div className="flex items-center gap-2">
            {/* Copy info button */}
            <button
              onClick={handleCopyLink}
              title="คัดลอกข้อมูลใบเสร็จ"
              className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl transition-colors flex items-center gap-1.5 border border-slate-700"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-400">คัดลอกแล้ว!</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">คัดลอกข้อมูล</span>
                </>
              )}
            </button>

            {/* Print button */}
            <button
              onClick={handlePrint}
              className="px-3.5 py-2 bg-blue-700 hover:bg-blue-600 text-white text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 shadow-sm shadow-blue-700/30 cursor-pointer"
            >
              <Printer className="w-4 h-4" />
              <span>พิมพ์ใบเสร็จ (Print)</span>
            </button>

            {/* Download PDF button */}
            <button
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 shadow-sm shadow-emerald-600/30 disabled:opacity-50 cursor-pointer"
            >
              {isGeneratingPDF ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>กำลังสร้าง PDF...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span>บันทึกเป็น PDF</span>
                </>
              )}
            </button>

            {/* Close modal button */}
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors ml-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Receipt Preview Canvas Area */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 bg-slate-100/90 print:p-0 print:overflow-visible print:bg-white">
          <ReceiptDocument bill={bill} receiptRef={receiptRef} />
        </div>

        {/* Bottom Helper Bar (Hidden on Print) */}
        <div className="px-6 py-3 bg-white border-t border-slate-200 flex items-center justify-between text-xs text-slate-500 print:hidden">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span>ใบเสร็จนี้ถูกบันทึกลงฐานข้อมูลและส่งให้ลูกบ้านตรวจสอบผ่าน Resident Portal แล้ว</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold transition-colors"
          >
            ปิดหน้าต่าง
          </button>
        </div>
      </div>
    </div>
  );
};
